import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import API from 'src/utils/api';
import { isValidEmail, isValidName, isValidPassword } from 'src/utils/forms';
import { LOCAL_STORAGE_TOKEN_KEY } from 'src/utils/constants';
import Input from 'src/components/input/Input';
import Button from 'src/components/button/Button';
import Checkbox from 'src/components/checkbox/Checkbox';
import logoIcon from 'src/icons/logo.svg';
import signupIllustration from 'src/icons/signup-illustration.svg';
import SecurityLevelCheckbox from 'src/components/security-level-checkbox/SecurityLevelCheckbox';
import Loader from 'src/components/loader/Loader';

const Signup = () => {
    // form fields
    const [firstName, setFirstName] = useState();
    const [lastName, setLastName] = useState();
    const [email, setEmail] = useState();
    const [password, setPassword] = useState();
    const [maxSecurityLevel, setMaxSecurityLevel] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    // UI state
    const [isMaxSecurityLevelModalOpen, setIsMaxSecurityLevelModalOpen] = useState(false);
    const [loading, setLoading] = useState();
    // error
    const [errors, setErrors] = useState();

    const checkErrors = useCallback(() => {
        let newErrors = {};
        if (!email || !isValidEmail(email)) newErrors.email = 'Please enter a valid email (max 255 characters)';
        if (!password || !isValidPassword(password)) {
            newErrors.password = 'Please enter a valid password (between 6 and 255 characters long)';
        }
        if (!firstName || !isValidName(firstName))
            newErrors.firstName = 'Please enter your first name (max 255 characters)';
        if (!lastName || !isValidName(lastName))
            newErrors.lastName = 'Please enter your last name (max 255 characters)';
        if (!agreeTerms) {
            newErrors.agreeTerms = 'Please agree to terms of use';
            toast('You need to agree to the terms of use and privacy policy to continue', { icon: '🚨' });
        }
        return newErrors;
    }, [email, password, firstName, lastName, agreeTerms]);

    useEffect(() => {
        if (email === undefined || password === undefined || firstName === undefined || lastName === undefined) return;
        setErrors(checkErrors());
    }, [email, password, firstName, lastName, checkErrors]);

    const onSubmit = async (e) => {
        e.preventDefault();

        let newErrors = checkErrors();
        if (Object.keys(newErrors).length !== 0) return setErrors(newErrors);

        setLoading(true);

        try {
            const response = await API.post('/auth/signup/', {
                email,
                password,
                firstName,
                lastName,
                securityLevel: maxSecurityLevel ? 1 : 2,
            });
            setLoading(false);
            localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, response.data.token);
            window.location.href = '/';
        } catch (error) {
            setLoading(false);
            if (error.response.status === 400) {
                if (error.response.data?.errors?.find((e) => e.msg === 'Email is already in use')) {
                    toast.error('This email is already in use');
                    return;
                }
                toast.error('There is an error on the form');
                return;
            }
            toast.error('Unexpected error, please try again');
        }
    };

    if (loading) return <Loader className="h-screen" />;

    return (
        <div>
            <div className="w-screen flex justify-center xl:absolute mt-4">
                <img src={logoIcon} alt="Payonix logo" width={40} />
            </div>
            <div className="flex items-center justify-center mt-16 xl:mt-0 w-screen xl:h-screen max-w-screen-2xl mx-auto">
                <form className="flex flex-col w-full px-8 xl:px-0 xl:w-1/3">
                    <strong className="text-4xl mb-4">Welcome to Payonix</strong>
                    <span className="mb-8">
                        Already have an account?&nbsp;
                        <Link to={'/login'} className="text-pink">
                            Log in
                        </Link>
                    </span>
                    <div className="flex w-full flex-col md:flex-row">
                        <Input
                            type="text"
                            value={firstName}
                            setValue={setFirstName}
                            label="First Name"
                            fieldName="firstName"
                            error={errors && errors['firstName']}
                            className="md:w-1/2 md:pr-4"
                        />
                        <Input
                            type="text"
                            value={lastName}
                            setValue={setLastName}
                            label="Last Name"
                            fieldName="lastName"
                            error={errors && errors['lastName']}
                            className="md:w-1/2 md:pl-4"
                        />
                    </div>
                    <Input
                        type="email"
                        value={email}
                        setValue={setEmail}
                        label="Email"
                        fieldName="email"
                        error={errors && errors['email']}
                    />
                    <Input
                        type="password"
                        value={password}
                        setValue={setPassword}
                        label="Password"
                        fieldName="password"
                        error={errors && errors['password']}
                    />
                    <SecurityLevelCheckbox
                        checked={maxSecurityLevel}
                        setChecked={setMaxSecurityLevel}
                        isModalOpen={isMaxSecurityLevelModalOpen}
                        setIsModalOpen={setIsMaxSecurityLevelModalOpen}
                    />
                    <label htmlFor="terms" className="flex items-center">
                        <Checkbox fieldName="terms" checked={agreeTerms} onChange={setAgreeTerms} className="mr-2" />
                        <span>
                            I agree to the&nbsp;
                            <a
                                href="https://payonix.org/terms"
                                target="_blank"
                                rel="noreferrer"
                                className="underline text-black"
                            >
                                Terms of use
                            </a>
                            &nbsp;and&nbsp;
                            <a
                                href="https://payonix.org/privacy-policy"
                                target="_blank"
                                rel="noreferrer"
                                className="underline text-black"
                            >
                                Privacy Policy
                            </a>
                        </span>
                    </label>
                    <Button primary onClick={onSubmit} text="Sign up" className="mt-8" />
                </form>
                <img
                    src={signupIllustration}
                    alt="sign up illustration"
                    className="hidden xl:block xl:w-1/3 xl:ml-48 p-8"
                />
            </div>
            <div className="w-screen flex justify-center xl:absolute mt-16 xl:mt-0 bottom-0 mb-4 text-gray-darker text-xs">
                © 2022 Payonix.com. All rights reserved
            </div>
        </div>
    );
};

export default Signup;
